// alterPackage — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'alter-package' })` op (DSE2 §3.D8).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D7 — Procedure / Function shell UI
// (oracle packages variant).
//
// Oracle-only. The portable surface is COMPILE [BODY] only — every other
// shape redefines via op:'create-package' { orReplace: true }.

import type { SqlEngine, QualifiedName } from '../../../../sql-grammar/model';
import type {
  DdlEdit,
  PackageChange,
} from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface AlterPackageArgs {
  readonly packageName: QualifiedName;
  readonly change: PackageChange;
}

export function buildAlterPackageEdit(args: AlterPackageArgs): DdlEdit {
  return {
    op: 'alter-package',
    packageName: args.packageName,
    change: args.change,
  };
}

export async function commitAlterPackage(
  ctx: VqeDdlEditContext, args: AlterPackageArgs,
): Promise<void> {
  const edit = buildAlterPackageEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

export function isAlterPackageSupported(engine: SqlEngine): boolean {
  return engine === 'oracle';
}

export function getAlterPackageUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'oracle') return '';
  return 'Packages are an Oracle-only construct.';
}

export function validateAlterPackageForEngine(
  engine: SqlEngine, args: AlterPackageArgs,
): string | null {
  if (!isAlterPackageSupported(engine)) {
    return getAlterPackageUnsupportedTooltip(engine);
  }
  if (args.packageName.name.trim().length === 0) {
    return 'Package name is required.';
  }
  if (args.change.recompile !== true) {
    return `Oracle ALTER PACKAGE only supports COMPILE — pick recompile.`;
  }
  return null;
}
